import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  output,
  booleanAttribute,
  computed,
  effect,
} from '@angular/core';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import { ENVIRONMENT_CONFIG, isBrandSegmentType } from '@core/config-angular';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { ContractType } from '@essent/common';
import { QuestionnaireResults } from '@innogy/retention/store';

import {
  CommodityOfferAlignment,
  CommodityOfferContent,
  type CommodityOfferLayoutType,
} from '../commodity-offers.model';

@Component({
  selector: 'wl-commodity-best-offer-list',
  templateUrl: './commodity-best-offer-list.component.html',
  styleUrls: [
    '../commodity-offer-list/commodity-offer-list.component.essent.scss',
  ],
})
export class CommodityBestOfferListComponent {
  readonly offers = input<Offer[]>();
  readonly content = input<CommodityOfferContent>();
  readonly showMemberGetMember = input(false, { transform: booleanAttribute });
  readonly alignment = input<CommodityOfferAlignment>();
  readonly questionnaireResults = input<QuestionnaireResults>();

  selectOffer = output<Offer>();
  openMoreInfo = output<Offer>();
  viewTariffs = output<Offer>();

  computedVariant?: CommodityOfferLayoutType;
  selectedContractType?: ContractType;

  private readonly envConfig = inject(ENVIRONMENT_CONFIG);
  private readonly mediaObserverService = inject(MediaObserverService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly isMijnConsumentEssent = computed(() =>
    isBrandSegmentType({
      brand: 'essent',
      segment: 'consument',
      type: 'mijn',
      currentBrand: this.envConfig.brand,
      currentSegment: this.envConfig.segment,
      currentType: this.envConfig.type,
    })
  );
  readonly offersExist = computed(
    () => this.offers() && (this.offers() as Offer[]).length > 0
  );

  readonly listContainsHighlights = computed(
    () =>
      !!this.offers()?.some(
        (offer) => offer.isHighlighted && offer.isHighlightedLabel
      )
  );

  readonly listContainsIncentives = computed(
    () => !!this.offers()?.some((offer) => offer.incentiveId)
  );

  readonly initiallyActiveOfferIds = computed(() => [
    (this.offers() ?? [])[0].offerId,
  ]);

  constructor() {
    effect(() => {
      const isDesktop = this.mediaObserverService.deviceInfo().isDesktop;
      if (this.alignment() === 'vertical' || !isDesktop) {
        this.computedVariant = 'accordion';
      } else {
        this.computedVariant = 'card';
      }

      // Manually trigger change detection
      this.changeDetectorRef.detectChanges();
    });
  }

  isActive(offer: Offer): boolean {
    return this.initiallyActiveOfferIds().includes(offer.offerId);
  }
}
